import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientService } from '../../../../core/services/client.service';
import { DiaryService } from '../../../../core/services/diary.service';
import { CaseService } from '../../../../core/services/case.service';
import { TodoForCreation } from '../../../../core/models/todos/todo-for-creation.model';
import { TodoForUpdation } from '../../../../core/models/todos/todo-for-updation.model';
import { TodoRequestFilter } from '../../../../core/models/todos/todo-request-filter.model';
import { ClientForListing } from '../../../../core/models/clients/client-for-listing.model';
import { DiaryForListing } from '../../../../core/models/diaries/diary-for-listing.model';
import { CaseForListing } from '../../../../core/models/case/case-for-listing.model';
import { TodoService } from '../../../../core/services/todo.service';

@Component({
  selector: 'app-todo-form',
  standalone: false,
  templateUrl: './todo-form.component.html',
  styleUrl: './todo-form.component.css'
})

export class TodoFormComponent implements OnInit {
  todoForm!: FormGroup;
  isEditMode = false;
  isSubmited = false;
  todoId!: number;
  clients: ClientForListing[] = [];
  diaries: DiaryForListing[] = [];
  cases: CaseForListing[] = [];

  constructor(
    private fb: FormBuilder,
    private todoService: TodoService,
    private clientService: ClientService,
    private diaryService: DiaryService,
    private caseService: CaseService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadClients();
    this.loadDiaries();
    this.loadCases();

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.todoId = +params['id'];
        this.loadTodoData(this.todoId);
      }
    });
  }

  private buildForm(): void {
    this.todoForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      dueDate: [null],
      isCompleted: [false],
      completedAt: [null],
      caseId: [null],
      clientId: [null],
      diaryId: [null]
    });
  }

  private loadClients(): void {
    this.clientService.getPaginatedClients({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.clients = res.data,
      error: () => this.snackBar.open('Failed to load clients', 'Close', { duration: 3000 })
    });
  }

  private loadDiaries(): void {
    this.diaryService.getPaginatedDiaries({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.diaries = res.data,
      error: () => this.snackBar.open('Failed to load diaries', 'Close', { duration: 3000 })
    });
  }

  private loadCases(): void {
    this.caseService.getPaginatedCases({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.cases = res.data,
      error: () => this.snackBar.open('Failed to load cases', 'Close', { duration: 3000 })
    });
  }

  private loadTodoData(id: number): void {
    this.todoService.getTodoById(id).subscribe({
      next: (todoData) => this.todoForm.patchValue(todoData),
      error: () => this.snackBar.open('Failed to load todo details', 'Close', { duration: 3000 })
    });
  }

  submitForm(): void {
    if (this.todoForm.invalid){
      this.isSubmited = false;
      return;
    }

    if (this.isEditMode) {
      const updatedTodo: TodoForUpdation = {
        id: this.todoId,
        ...this.todoForm.value
      };

      this.todoService.updateTodo(updatedTodo).subscribe({
        next: () => {
          this.snackBar.open('Todo updated successfully', 'Close', { duration: 3000 });
          this.router.navigate(['/todos']);
        },
        error: () => {
          this.snackBar.open('Failed to update todo', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });

    } else {
      const newTodo: TodoForCreation = this.todoForm.value;

      this.todoService.createTodo(newTodo).subscribe({
        next: () => {
          this.snackBar.open('Todo added successfully', 'Close', { duration: 3000 });
          this.router.navigate(['/todos']);
        },
        error: () => {
          this.snackBar.open('Failed to add todo', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    }
  }
}

